import React, { createContext, useContext, useState, useEffect } from "react";
import { getCookie, setCookieMinutes, deleteCookie } from "../utils/CookieUtil";
import { getUserRoles } from "../../../utils/jwtUtils";
import UserAPI from "../services/UserAPI";
import UserAuthAPI from "../services/UserAuthAPI";
import APIService from "../services/APIService";
import { SESSION_TTL_MINUTES } from "../../../config/sessionConfig";
import {
  clearFrontendAuthData,
  hasFrontendAuthData,
  getCookie as getAuthCookie,
  setGlobalLogoutCallback,
} from "../utils/AuthUtil";

// Create the UserContext
const UserContext = createContext();

// Custom hook to use the UserContext
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};

// UserProvider component to wrap the app and provide user state
export const UserProvider = ({ children }) => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [userName, setUserName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSessionTimedOut, setIsSessionTimedOut] = useState(false);
  const [isDemoSeat, setIsDemoSeat] = useState(() =>
    getUserRoles().includes("demo_seat"),
  );
  // Chrome Web Store reviewer seat — locked to the browser extension. The web app
  // shell hides all functional features for this role (backend enforces the real
  // restriction via ExtensionReviewAccessFilter).
  const [isExtensionReview, setIsExtensionReview] = useState(() =>
    getUserRoles().includes("extension_review"),
  );

  // Read-only view state: true when account owner is viewing a consented member's data
  const [isReadOnlyView, setIsReadOnlyView] = useState(false);
  // Consent status for the currently selected member (when owner is viewing)
  const [memberConsentStatus, setMemberConsentStatus] = useState({
    dashboardAccess: false,
    analysisAccess: false,
  });

  // Keep isDemoSeat in sync whenever roles change (login, logout, 10-min server sync)
  useEffect(() => {
    const sync = () => {
      setIsDemoSeat(getUserRoles().includes("demo_seat"));
      setIsExtensionReview(getUserRoles().includes("extension_review"));
    };
    const syncStorage = (e) => {
      if (e.key === "roles") sync();
    };
    window.addEventListener("userRolesChanged", sync);
    window.addEventListener("userLoggedIn", sync);
    window.addEventListener("userLoggedOut", sync);
    window.addEventListener("storage", syncStorage);
    return () => {
      window.removeEventListener("userRolesChanged", sync);
      window.removeEventListener("userLoggedIn", sync);
      window.removeEventListener("userLoggedOut", sync);
      window.removeEventListener("storage", syncStorage);
    };
  }, []);

  const cookieTTLMinutes = SESSION_TTL_MINUTES;

  // Debug state changes
  useEffect(() => {
    console.log("🔄 UserContext: selectedUser state changed:", selectedUser);
  }, [selectedUser]);

  useEffect(() => {
    console.log("🔄 UserContext: loggedInUser state changed:", loggedInUser);
  }, [loggedInUser]);

  useEffect(() => {
    console.log("🔄 UserContext: userName state changed:", userName);
  }, [userName]);

  useEffect(() => {
    console.log("🔄 UserContext: isLoading state changed:", isLoading);
  }, [isLoading]);

  // Load user from cookie on initialization and validate session
  useEffect(() => {
    console.log(
      "🚀 UserContext: Component mounted, loading users from cookies...",
    );

    // Register global logout callback for AuthUtil
    setGlobalLogoutCallback(() => {
      console.log(
        "🔔 UserContext: Received global logout callback from AuthUtil - Session timed out",
      );
      handleAuthenticationLoss();
      setIsSessionTimedOut(true); // Also set session timeout flag for UI
    });

    loadUserFromCookie();

    // Also check backend session validity
    validateBackendSession();

    // Set up periodic role validation for all users
    const setupRoleValidation = () => {
      const userUuid = getCookie("uuid");
      const storedRoles = localStorage.getItem("roles");

      if (userUuid && storedRoles) {
        console.log(
          "🔄 UserContext: Setting up periodic role validation for user",
        );

        // Validate roles every 10 minutes for all users to prevent role loss
        const roleValidationInterval = setInterval(
          async () => {
            const currentUuid = getCookie("uuid");
            if (currentUuid) {
              console.log(
                "🔄 UserContext: Performing periodic role validation",
              );
              await validateAndSyncUserRoles(currentUuid);
            } else {
              console.log(
                "� UserContext: No UUID found, clearing role validation interval",
              );
              clearInterval(roleValidationInterval);
            }
          },
          10 * 60 * 1000,
        ); // 10 minutes

        // Store interval ID for cleanup
        window.userRoleValidationInterval = roleValidationInterval;

        console.log(
          "✅ UserContext: Periodic role validation set up (10 minute intervals)",
        );
      } else {
        console.log(
          "� UserContext: No user session or roles found, skipping role validation setup",
        );
      }
    };

    // Setup role validation with a slight delay to allow initial loading
    setTimeout(setupRoleValidation, 2000);

    // Cleanup function
    return () => {
      console.log(
        "🧹 UserContext: Component unmounting, clearing global logout callback",
      );
      setGlobalLogoutCallback(null);
    };
  }, []);

  // Function to load user from cookie
  const loadUserFromCookie = async () => {
    try {
      console.log("📂 UserContext: Starting loadUserFromCookie...");

      // Check if authentication is in progress
      if (window.authenticationPromise) {
        console.log(
          "⏸️ UserContext: Authentication in progress, waiting for completion...",
        );
        await window.authenticationPromise;
        console.log(
          "✅ UserContext: Authentication complete, proceeding with cookie load",
        );
      }

      setIsLoading(true);
      console.log("⏳ UserContext: Set loading to true");

      // Debug: Check all cookies
      console.log("🍪 UserContext: All available cookies:", document.cookie);

      // Load selected user from "user" cookie
      const userUuid = getCookie("user");
      console.log("🔍 UserContext: Retrieved 'user' cookie:", userUuid);

      if (userUuid) {
        console.log(
          "📥 UserContext: Loading selected user from cookie:",
          userUuid,
        );
        try {
          const userData = await UserAPI.getUserByUuid(userUuid);
          console.log(
            "📦 UserContext: API response for selected user:",
            userData,
          );

          if (userData && userData.uuid) {
            console.log(
              "✅ UserContext: Valid selected user data received, calling selectUser...",
            );
            selectUser(userData, false); // Don't update cookie since we're loading from it
          } else {
            console.log("❌ UserContext: Invalid selected user data received");
          }
        } catch (error) {
          console.error("❌ UserContext: Error fetching selected user:", error);
        }
      } else {
        console.log("🚫 UserContext: No 'user' cookie found");
      }

      // Load logged-in user from "uuid" cookie
      const loggedInUuid = getCookie("uuid");
      console.log("🔍 UserContext: Retrieved 'uuid' cookie:", loggedInUuid);

      if (loggedInUuid) {
        console.log(
          "📥 UserContext: Loading logged-in user from cookie:",
          loggedInUuid,
        );
        try {
          const loggedInUserData = await UserAPI.getUserByUuid(loggedInUuid);
          console.log(
            "📦 UserContext: API response for logged-in user:",
            loggedInUserData,
          );

          if (loggedInUserData && loggedInUserData.uuid) {
            console.log(
              "✅ UserContext: Valid logged-in user data received, setting state...",
            );
            setLoggedInUser(loggedInUserData);
          } else {
            console.log("❌ UserContext: Invalid logged-in user data received");
            // Clear stale authentication data if API returned null/invalid data
            console.log(
              "🧹 UserContext: Clearing stale authentication data...",
            );
            clearStaleAuthenticationData();
          }
        } catch (error) {
          console.error(
            "❌ UserContext: Error fetching logged-in user:",
            error,
          );

          // Check if it's an authentication error (401, 403)
          if (error.response && [401, 403].includes(error.response.status)) {
            console.log(
              "🚨 UserContext: Authentication error detected, clearing stale session data",
            );
            clearStaleAuthenticationData();
          }
        }
      } else {
        console.log("🚫 UserContext: No 'uuid' cookie found");
      }
    } catch (error) {
      console.error("❌ UserContext: Error in loadUserFromCookie:", error);
    } finally {
      console.log("⏳ UserContext: Setting loading to false");
      setIsLoading(false);
      console.log("✅ UserContext: loadUserFromCookie completed");
    }
  };

  // Function to handle user login
  const loginUser = async (user) => {
    console.log("🔐 UserContext: loginUser called with:", user);

    if (user && user.uuid) {
      console.log("✅ UserContext: Valid user provided for login");

      // Check if authentication promise already exists (set by LoginFormModal)
      if (!window.authenticationPromise) {
        console.log(
          "⚠️ UserContext: No authentication promise found, creating one",
        );
        window.authenticationStarting = true;
        window.authenticationPromise = new Promise((resolve) => {
          window.resolveAuthentication = resolve;
        });
      }
      console.log("🔒 UserContext: Authentication promise created");

      // Now update state
      console.log("📝 UserContext: Setting loggedInUser state...");
      setLoggedInUser(user);
      setIsSessionTimedOut(false); // Reset session timeout state on successful login

      // Set the uuid cookie for the logged-in user with environment-specific TTL
      console.log(
        "🍪 UserContext: Setting 'uuid' cookie:",
        user.uuid,
        "TTL:",
        cookieTTLMinutes,
        "minutes",
      );
      setCookieMinutes("uuid", user.uuid, cookieTTLMinutes, "/", false, "lax");

      // Also set the user cookie that many components expect
      console.log(
        "🍪 UserContext: Setting 'user' cookie:",
        user.uuid,
        "TTL:",
        cookieTTLMinutes,
        "minutes",
      );
      setCookieMinutes("user", user.uuid, cookieTTLMinutes, "/", false, "lax");

      // Clear authentication flags (promise should already be resolved by LoginFormModal)
      console.log("🏁 UserContext: Clearing authentication flags");
      window.authenticationPromise = null;
      window.authenticationStarting = false;
      window.resolveAuthentication = null;

      // Dispatch authentication complete event
      const authCompleteEvent = new CustomEvent("authenticationComplete", {
        detail: { success: true },
      });
      window.dispatchEvent(authCompleteEvent);

      // Note: sessionId is now set as HttpOnly cookie by the server
      // No need to manually set it in frontend anymore
      console.log(
        "🍪 UserContext: Session ID is automatically managed as HttpOnly cookie by server",
      );

      console.log(
        "✅ UserContext: User logged in successfully:",
        user.firstName,
        user.lastName,
      );

      // NOTE: We intentionally do NOT call getUserByUuid here right after login.
      // The login response from the server already contains the full user profile
      // (including the 'active' field). Making an immediate API call to /users/uuid/{id}
      // right after login creates a race condition where the HttpOnly JWT cookie may not
      // yet have propagated through all API Gateway routing layers, causing a 401 that
      // in turn triggers clearFrontendAuthData → cascade of logoutUser() calls.
      // If a fresh user profile is needed later (e.g. after a settings change), it can
      // be fetched at that time with a proper retry mechanism.

      // Dispatch userLoggedIn event for components like MessageBar
      console.log("📢 UserContext: Dispatching 'userLoggedIn' event...");
      const loginEvent = new CustomEvent("userLoggedIn", {
        detail: {
          user: user,
          uuid: user.uuid,
          fullName: `${user.firstName} ${user.lastName}`,
        },
      });
      window.dispatchEvent(loginEvent);
      console.log("✅ UserContext: 'userLoggedIn' event dispatched");
    } else {
      console.log("❌ UserContext: Invalid user provided for login:", user);
      // Clean up authentication flags just in case
      window.authenticationStarting = false;
      window.authenticationPromise = null;
    }
  };

  // Function to select a user
  const selectUser = (user, updateCookie = true, dispatchEvent = true) => {
    console.log("👤 UserContext: selectUser called with:", {
      user: user,
      updateCookie: updateCookie,
      dispatchEvent: dispatchEvent,
    });

    if (user && user.verified === false) {
      console.warn("⚠️ UserContext: Cannot select unverified user:", user);
      return;
    }

    console.log("📝 UserContext: Setting selectedUser state...");
    setSelectedUser(user);

    if (user) {
      const fullName = `${user.firstName} ${user.lastName}`;
      console.log("📝 UserContext: Setting userName to:", fullName);
      setUserName(fullName);

      // Update localStorage for backward compatibility
      console.log("💾 UserContext: Updating localStorage with userName");
      localStorage.setItem("userName", fullName);

      // Update cookie if requested
      if (updateCookie) {
        console.log(
          "🍪 UserContext: Updating 'user' cookie with UUID:",
          user.uuid,
          "TTL:",
          cookieTTLMinutes,
          "minutes",
        );
        setCookieMinutes(
          "user",
          user.uuid,
          cookieTTLMinutes,
          "/",
          false,
          "lax",
        );
      } else {
        console.log(
          "🚫 UserContext: Skipping cookie update (updateCookie=false)",
        );
      }

      console.log("✅ UserContext: User selected successfully:", fullName);
    } else {
      // Clear selection
      console.log("🧹 UserContext: Clearing user selection...");
      console.log("📝 UserContext: Clearing userName");
      setUserName("");
      console.log("💾 UserContext: Removing userName from localStorage");
      localStorage.removeItem("userName");

      if (updateCookie) {
        console.log("🍪 UserContext: Clearing 'user' cookie");
        document.cookie =
          "user=; path=/; samesite=strict; expires=Thu, 01 Jan 1970 00:00:00 GMT";
      } else {
        console.log(
          "🚫 UserContext: Skipping cookie clear (updateCookie=false)",
        );
      }

      console.log("✅ UserContext: User selection cleared");
    }

    // Dispatch custom event for backward compatibility with existing components
    // Only dispatch if explicitly requested to prevent infinite loops
    if (dispatchEvent) {
      console.log("📢 UserContext: Dispatching 'userSelected' event...");
      const userSelectionEvent = new CustomEvent("userSelected", {
        detail: {
          userName: user ? `${user.firstName} ${user.lastName}` : "",
          uuid: user ? user.uuid : null,
          user: user,
        },
      });
      window.dispatchEvent(userSelectionEvent);
      console.log("✅ UserContext: 'userSelected' event dispatched");
    } else {
      console.log(
        "🚫 UserContext: Skipping event dispatch (dispatchEvent=false)",
      );
    }
  };

  /**
   * Enter read-only view mode for a consented member user.
   * Called by the account owner when they click "View Dashboard" on a consented member.
   * This is separate from the normal selectUser flow used in member administration.
   */
  const enterReadOnlyView = (memberUser, consentStatus) => {
    console.log(
      "👁️ UserContext: enterReadOnlyView called for:",
      memberUser?.firstName,
      memberUser?.lastName,
    );
    if (!memberUser || !memberUser.uuid) {
      console.warn("⚠️ UserContext: Invalid member user for read-only view");
      return;
    }
    setMemberConsentStatus(
      consentStatus || { dashboardAccess: false, analysisAccess: false },
    );
    setIsReadOnlyView(true);
    selectUser(memberUser);
    console.log(
      "✅ UserContext: Read-only view entered for member:",
      memberUser.firstName,
    );
  };

  /**
   * Exit read-only view mode and return to the account owner's own context.
   */
  const exitReadOnlyView = () => {
    console.log("👁️ UserContext: exitReadOnlyView called");
    setIsReadOnlyView(false);
    setMemberConsentStatus({ dashboardAccess: false, analysisAccess: false });
    if (loggedInUser) {
      selectUser(loggedInUser);
    }
    console.log(
      "✅ UserContext: Read-only view exited, returned to owner context",
    );
  };

  // Function to clear user selection
  const clearUserSelection = () => {
    console.log("🧹 UserContext: clearUserSelection called");
    setIsReadOnlyView(false);
    setMemberConsentStatus({ dashboardAccess: false, analysisAccess: false });
    selectUser(null);
  };

  // Function to clear stale authentication data when session is invalid
  const clearStaleAuthenticationData = () => {
    console.log("🧹 UserContext: clearStaleAuthenticationData called");

    // Before clearing everything, try one more session validation to be sure
    console.log(
      "🔄 UserContext: Performing final session validation before clearing data",
    );

    // Clear state
    console.log("📝 UserContext: Clearing loggedInUser state...");
    setLoggedInUser(null);

    console.log("📝 UserContext: Clearing selectedUser state...");
    setSelectedUser(null);
    setUserName("");

    // Clear role validation interval since session is invalid
    if (window.userRoleValidationInterval) {
      console.log(
        "🧹 UserContext: Clearing role validation interval due to invalid session",
      );
      clearInterval(window.userRoleValidationInterval);
      window.userRoleValidationInterval = null;
    }

    // Clear frontend auth data using utility (this includes localStorage roles)
    clearFrontendAuthData();

    console.log("✅ UserContext: Stale authentication data cleared");
  };

  // Function to validate backend session using HttpOnly cookies
  const validateBackendSession = async () => {
    console.log("🔍 UserContext: Validating backend session...");

    // Only check if we have frontend authentication data
    if (!hasFrontendAuthData()) {
      console.log(
        "🚫 UserContext: No frontend auth data found, skipping backend validation",
      );
      return;
    }

    try {
      const sessionStatus = await UserAuthAPI.checkSessionStatus();

      if (sessionStatus.success && sessionStatus.authenticated) {
        console.log(
          "✅ UserContext: Backend session is valid:",
          sessionStatus.userUuid,
        );

        // Session is valid but check if roles match
        await validateAndSyncUserRoles(sessionStatus.userUuid);
      } else {
        console.log(
          "🚨 UserContext: Backend session invalid/expired but frontend auth data exists",
        );
        console.log(
          "🧹 UserContext: Clearing stale frontend authentication data...",
        );
        clearStaleAuthenticationData();
      }
    } catch (error) {
      console.error("❌ UserContext: Error validating backend session:", error);

      // If we can't reach the backend, assume stale session and clear frontend data
      if (hasFrontendAuthData()) {
        console.log(
          "🧹 UserContext: Backend unreachable, clearing potentially stale frontend data...",
        );
        clearStaleAuthenticationData();
      }
    }
  };

  // Function to validate and sync user roles when session is valid
  const validateAndSyncUserRoles = async (userUuid) => {
    try {
      console.log("🔍 UserContext: Validating user roles for UUID:", userUuid);

      // Get current roles from localStorage
      const storedRoles = localStorage.getItem("roles");
      let currentRoles = [];

      if (storedRoles) {
        try {
          currentRoles = JSON.parse(storedRoles);
        } catch (e) {
          console.warn(
            "⚠️ UserContext: Invalid roles in localStorage, will refresh",
          );
          currentRoles = [];
        }
      }

      // Get fresh roles from backend using the shared authenticated APIService.
      // This MUST go through APIService (not a raw fetch) because APIService
      // attaches the `Authorization: Bearer <token>` fallback that the rest of
      // the app relies on. A raw fetch with cookie-only credentials silently
      // fails (401/403) on browser/deployment combinations that drop the
      // cross-origin HttpOnly authToken cookie — in which case this method used
      // to "preserve existing roles", leaving stale roles in localStorage and
      // showing a freshly-granted account_admin user as a plain Member.
      const roleData = await APIService.get("/api/v1/base/roles/my-roles");

      // Only sync when the server returned a valid roles array. Anything else
      // (null/404/missing field) is treated as "could not fetch" so we never
      // clobber good roles with an empty list.
      if (roleData && Array.isArray(roleData.roles)) {
        const freshRoles = roleData.roles;

        console.log("🔄 UserContext: Current stored roles:", currentRoles);
        console.log("🔄 UserContext: Fresh roles from backend:", freshRoles);

        // Check if roles have changed
        const rolesChanged =
          JSON.stringify(currentRoles.sort()) !==
          JSON.stringify(freshRoles.sort());

        if (rolesChanged) {
          console.log(
            "🔄 UserContext: User roles have changed, updating localStorage",
          );
          localStorage.setItem("roles", JSON.stringify(freshRoles));

          // Log specific role changes
          const addedRoles = freshRoles.filter(
            (role) => !currentRoles.includes(role),
          );
          const removedRoles = currentRoles.filter(
            (role) => !freshRoles.includes(role),
          );

          if (addedRoles.length > 0) {
            console.log("✅ UserContext: Roles added:", addedRoles);
          }
          if (removedRoles.length > 0) {
            console.log("⚠️ UserContext: Roles removed:", removedRoles);
          }

          // Dispatch role change event for components that depend on roles
          const roleChangeEvent = new CustomEvent("userRolesChanged", {
            detail: {
              previousRoles: currentRoles,
              newRoles: freshRoles,
              userUuid: userUuid,
              addedRoles: addedRoles,
              removedRoles: removedRoles,
            },
          });
          window.dispatchEvent(roleChangeEvent);
        } else {
          console.log("✅ UserContext: User roles are up to date");
        }
      } else {
        console.warn(
          "⚠️ UserContext: my-roles response did not contain a roles array; preserving existing roles",
        );
        // If we can't get fresh roles but session is valid, keep existing roles
        console.log(
          "🛡️ UserContext: Preserving existing roles since session is valid",
        );
      }
    } catch (error) {
      console.error("❌ UserContext: Error validating user roles:", error);
      // If there's an error but session is valid, preserve existing roles
      console.log(
        "🛡️ UserContext: Preserving existing roles due to validation error",
      );
    }
  };

  // Function to update logged-in user
  const updateLoggedInUser = (user) => {
    console.log("🔄 UserContext: updateLoggedInUser called with:", user);
    setLoggedInUser(user);
  };

  // Function to handle user logout
  /**
   * Handle authentication loss (session expiry, token invalid, etc.)
   * This is called by AuthUtil's clearFrontendAuthData as a callback
   * DO NOT call clearFrontendAuthData from here to avoid infinite loop
   */
  const handleAuthenticationLoss = () => {
    console.log("🚨 UserContext: handleAuthenticationLoss called");

    // Clear state
    setLoggedInUser(null);
    setSelectedUser(null);
    setUserName("");

    // Clear role validation interval
    if (window.userRoleValidationInterval) {
      clearInterval(window.userRoleValidationInterval);
      window.userRoleValidationInterval = null;
    }

    // Trigger sign-in modal (set after state cleanup so it persists)
    localStorage.setItem("showSignInModal", "true");

    // Dispatch event for other components
    const logoutEvent = new CustomEvent("userLoggedOut");
    window.dispatchEvent(logoutEvent);

    console.log(
      "✅ UserContext: Authentication loss handled, sign-in modal triggered",
    );
  };

  const logoutUser = async (opts = {}) => {
    console.log("🚪 UserContext: logoutUser called", opts);

    // Clean up any authentication flags
    window.authenticationStarting = false;
    window.authenticationPromise = null;
    window.resolveAuthentication = null;

    try {
      // Call UserAuthAPI logout to handle backend logout and localStorage cleanup.
      // Forward force/reason so explicit user-initiated logouts can bypass the
      // post-login grace window in UserAuthAPI.logout.
      const result = await UserAuthAPI.logout(opts);
      console.log("🌐 UserContext: Backend logout result:", result);

      // If the logout was suppressed by the post-login guard, do NOT proceed
      // to tear down React state — that would leave the user looking signed
      // out in the UI even though their session is fully valid.
      if (result && result.suppressed) {
        console.warn(
          "🛑 UserContext: logoutUser aborted — call was suppressed by post-login grace window",
        );
        return result;
      }
    } catch (error) {
      console.error("❌ UserContext: Error during logout:", error);
      // Continue with frontend cleanup even if backend fails
    }

    console.log("📝 UserContext: Clearing loggedInUser state...");
    setLoggedInUser(null);

    console.log("📝 UserContext: Clearing selectedUser state...");
    clearUserSelection();

    // Clear all authentication-related cookies
    console.log("🍪 UserContext: Clearing authentication cookies...");
    deleteCookie("uuid");
    deleteCookie("user");
    deleteCookie("authToken");
    deleteCookie("sessionId");

    // Clear additional localStorage items that should be removed on logout
    console.log("💾 UserContext: Clearing additional localStorage items...");
    localStorage.removeItem("dashboardView");
    localStorage.removeItem("userName");
    localStorage.removeItem("ada-contrast");
    localStorage.removeItem("ada-high-contrast");
    localStorage.removeItem("analyticsEnabled");
    localStorage.removeItem("dashboardMode");
    localStorage.removeItem("dateFormat");
    localStorage.removeItem("emailMarketing");
    localStorage.removeItem("emailSecurityAlerts");
    localStorage.removeItem("emailTrainingReminders");
    localStorage.removeItem("emailWeeklyDigest");
    localStorage.removeItem("gettingStarted_explored");
    localStorage.removeItem("landingPage");
    localStorage.removeItem("language");
    localStorage.removeItem("loginNotificationsEnabled");
    localStorage.removeItem("profileVisibility");
    localStorage.removeItem("pushNotificationsEnabled");
    localStorage.removeItem("showViewSettings");
    localStorage.removeItem("theme");
    localStorage.removeItem("timeFormat");
    localStorage.removeItem("timezone");
    localStorage.removeItem("tryitout_fingerprint");

    // Clear role validation interval
    if (window.userRoleValidationInterval) {
      console.log("🧹 UserContext: Clearing role validation interval...");
      clearInterval(window.userRoleValidationInterval);
      window.userRoleValidationInterval = null;
    }

    console.log(
      "✅ UserContext: User logged out and all data cleared successfully",
    );

    // Dispatch userLoggedOut event for components like MessageBar
    console.log("📢 UserContext: Dispatching 'userLoggedOut' event...");
    const logoutEvent = new CustomEvent("userLoggedOut");
    window.dispatchEvent(logoutEvent);
    console.log("✅ UserContext: 'userLoggedOut' event dispatched");
  };

  // Listen for external user selection events (for backward compatibility)
  useEffect(() => {
    console.log("👂 UserContext: Setting up external event listeners...");

    const handleExternalUserSelection = (event) => {
      console.log(
        "📨 UserContext: External user selection event detected:",
        event.detail,
      );
      if (event.detail && event.detail.user) {
        console.log(
          "✅ UserContext: Valid user in external event, calling selectUser...",
        );
        selectUser(event.detail.user, false, false); // Don't update cookie and don't dispatch event to prevent infinite loop
      } else {
        console.log("❌ UserContext: Invalid user in external event");
      }
    };

    window.addEventListener("userSelected", handleExternalUserSelection);
    console.log("✅ UserContext: External event listener added");

    return () => {
      console.log("🧹 UserContext: Cleaning up external event listener...");
      window.removeEventListener("userSelected", handleExternalUserSelection);
      console.log("✅ UserContext: External event listener removed");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper function to check if logged-in user is selected
  const isLoggedInUserSelected =
    loggedInUser && selectedUser && loggedInUser.uuid === selectedUser.uuid;

  // Debug context value changes
  const value = {
    selectedUser,
    loggedInUser,
    userName,
    isLoading,
    isSessionTimedOut,
    isLoggedInUserSelected,
    selectUser,
    clearUserSelection,
    updateLoggedInUser,
    loadUserFromCookie,
    loginUser, // Add this to the context value
    logoutUser, // Add this to the context value
    handleAuthenticationLoss, // Centralized auth loss handler
    setIsSessionTimedOut, // Add session timeout control
    isDemoSeat, // Boolean — true if logged-in user has demo_seat role
    isExtensionReview, // Boolean — true if logged-in user is a Chrome Web Store reviewer seat
    // Owner read-only access
    isReadOnlyView,
    memberConsentStatus,
    enterReadOnlyView,
    exitReadOnlyView,
  };

  console.log("🎯 UserContext: Providing context value:", {
    selectedUser: selectedUser
      ? `${selectedUser.firstName} ${selectedUser.lastName}`
      : null,
    loggedInUser: loggedInUser
      ? `${loggedInUser.firstName} ${loggedInUser.lastName}`
      : null,
    userName,
    isLoading,
    isLoggedInUserSelected,
  });

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export default UserContext;
